/*! Size & Fit Finder — maps a Band + Cup pair to a letter size and drives the product's size option. */
(function () {
  'use strict';

  var normalize = function (value) {
    return String(value == null ? '' : value).replace(/\s+/g, '').toUpperCase();
  };

  // Events that only ever come from a person, used to tell start-up apart from a real choice.
  var INTERACTIONS = ['pointerdown', 'keydown', 'change'];

  var toArray = function (list) {
    return Array.prototype.slice.call(list || []);
  };

  class SizeFitFinder extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.shouldSync = this.dataset.sync === 'true';
      this.syncBundle = this.dataset.syncBundle !== 'false';
      this.requireSelection = this.dataset.requireSelection === 'true';
      this.syncing = false;

      this.bandSelect = this.querySelector('[data-sff-band]');
      this.cupSelect = this.querySelector('[data-sff-cup]');
      this.sizeSelect = this.querySelector('[data-sff-size]');
      this.currentEl = this.querySelector('[data-sff-current]');
      this.fitsEl = this.querySelector('[data-sff-fits]');
      this.fitsNameEl = this.querySelector('[data-sff-fits-name]');
      this.fitsListEl = this.querySelector('[data-sff-fits-list]');
      this.noteEl = this.querySelector('[data-sff-note]');

      this.buildMaps();
      this.bindEvents();

      this.refreshAvailability();
      this.renderFits(this.selectedSize());
      this.prefillFromSize(this.selectedSize());
    }

    disconnectedCallback() {
      if (this.unsubscribeVariant) this.unsubscribeVariant();
      if (this.eventHost) this.eventHost.removeEventListener('variant:change', this.onExternalVariantChange);
      if (this.submitGuard) document.removeEventListener('submit', this.submitGuard, true);

      if (this.openOnInteraction) {
        INTERACTIONS.forEach(
          function (type) {
            document.removeEventListener(type, this.openOnInteraction, true);
          }.bind(this)
        );
      }
    }

    /* ---------- data ---------- */

    buildMaps() {
      var raw = {};

      try {
        var node = this.querySelector('[data-sff-map]');
        if (node) raw = JSON.parse(node.textContent) || {};
      } catch (error) {
        console.warn('[size-fit-finder] Could not parse the size mapping.', error);
      }

      this.fitsBySize = {};
      this.sizeByCombo = {};

      Object.keys(raw).forEach(
        function (sizeName) {
          var combos = (raw[sizeName] || []).filter(Boolean);
          if (!combos.length) return;

          this.fitsBySize[normalize(sizeName)] = { label: sizeName, combos: combos };

          combos.forEach(
            function (combo) {
              this.sizeByCombo[normalize(combo)] = sizeName;
            }.bind(this)
          );
        }.bind(this)
      );
    }

    /* ---------- wiring ---------- */

    bindEvents() {
      if (this.bandSelect) this.bandSelect.addEventListener('change', this.onPairChange.bind(this));
      if (this.cupSelect) this.cupSelect.addEventListener('change', this.onPairChange.bind(this));
      if (this.sizeSelect) this.sizeSelect.addEventListener('change', this.onSizeChange.bind(this));

      // The theme fires `variant:change` on the product form, not on the document
      // (assets/theme.js — `(this.productForm ?? document).dispatchEvent(...)`, no bubbling).
      this.onExternalVariantChange = this.onExternalVariantChange.bind(this);
      this.eventHost = this.productForm() || document;
      this.eventHost.addEventListener('variant:change', this.onExternalVariantChange);

      var pubsub = window.theme && window.theme.pubsub;

      if (pubsub && pubsub.subscribe && pubsub.PUB_SUB_EVENTS) {
        this.unsubscribeVariant = pubsub.subscribe(
          pubsub.PUB_SUB_EVENTS.variantChange,
          function (payload) {
            var variant = payload && payload.data && payload.data.variant;
            if (variant) this.adoptVariant(variant);
          }.bind(this)
        );
      }

      if (this.requireSelection) {
        // Capture on document so this lands before ProductForm's own target-phase submit
        // listener — listeners on the target itself run in registration order, not by phase.
        this.submitGuard = this.onFormSubmit.bind(this);
        document.addEventListener('submit', this.submitGuard, true);
      }

      this.openListening();
    }

    /**
     * Blocks on the page announce a variant during their own start-up — the Buy 1, 2, 3 block
     * resolves one from its selects and fires `variant:change` from its DOMContentLoaded pass.
     * Adopting that would fill in a size the shopper never chose, so start listening only once
     * a person has actually touched the page.
     */
    openListening() {
      if (!this.requireSelection) {
        this.ready = true;
        return;
      }

      this.ready = false;

      // Start-up runs to completion without anyone touching the page, so the first real
      // interaction is the dividing line. Capture phase, so it lands before the handler that
      // turns that interaction into a `variant:change`.
      this.openOnInteraction = function () {
        this.ready = true;
        INTERACTIONS.forEach(
          function (type) {
            document.removeEventListener(type, this.openOnInteraction, true);
          }.bind(this)
        );
      }.bind(this);

      INTERACTIONS.forEach(
        function (type) {
          document.addEventListener(type, this.openOnInteraction, true);
        }.bind(this)
      );
    }

    /* ---------- handlers ---------- */

    onPairChange(event) {
      this.refreshAvailability();

      var band = this.bandSelect ? this.bandSelect.value : '';
      var cup = this.cupSelect ? this.cupSelect.value : '';

      if (!band || !cup) {
        this.setNote('');
        return;
      }

      var match = this.sizeByCombo[normalize(band + cup)];

      if (!match) {
        this.setNote(this.dataset.noMatch || '');
        return;
      }

      if (!this.applySize(match, event)) {
        this.setNote(this.dataset.noMatch || '');
        return;
      }

      this.setNote('');
    }

    onSizeChange(event) {
      var size = this.selectedSize();

      this.setNote('');
      if (size) this.clearSelectionError();
      this.renderFits(size);
      this.clearPairIfMismatched(size);
      this.propagate(size, event);
    }

    onExternalVariantChange(event) {
      var variant = event && event.detail && event.detail.variant;
      if (variant) this.adoptVariant(variant);
    }

    /* ---------- "choose a size first" guard ---------- */

    onFormSubmit(event) {
      if (event.target !== this.productForm()) return;
      if (this.selectedSize()) return;

      event.preventDefault();
      event.stopPropagation();
      this.showSelectionError();
    }

    /** The theme's own error slots — one in the buy buttons, one in the sticky bar. */
    errorSlots() {
      var slots = [];
      var form = this.productForm();
      var host = this.host();

      if (form) slots = slots.concat(toArray(form.querySelectorAll('.product-form__error-message')));

      if (host) {
        var sticky = document.getElementById(
          'ProductStickyForm-' + host.getAttribute('data-section-id') + '-' + host.getAttribute('data-product-id')
        );
        if (sticky) slots = slots.concat(toArray(sticky.querySelectorAll('.product-form__error-message')));
      }

      return slots;
    }

    showSelectionError() {
      var message = this.dataset.selectError || '';

      this.errorSlots().forEach(function (slot) {
        slot.textContent = message;
        slot.hidden = !message;
      });

      // The shopper may have pressed the sticky bar with this block scrolled out of view.
      this.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (this.sizeSelect) this.sizeSelect.focus({ preventScroll: true });
    }

    clearSelectionError() {
      this.errorSlots().forEach(function (slot) {
        slot.textContent = '';
        slot.hidden = true;
      });
    }

    /* ---------- behaviour ---------- */

    selectedSize() {
      return this.sizeSelect ? this.sizeSelect.value : this.dataset.currentSize || '';
    }

    /** Point the size select at `size`, then refresh the label, summary and variant. */
    applySize(size, event) {
      var applied = this.setSizeSelect(size);
      if (!applied) return false;

      this.clearSelectionError();
      this.renderFits(applied);
      this.propagate(applied, event);

      return true;
    }

    setSizeSelect(size) {
      if (!this.sizeSelect) return '';

      var wanted = normalize(size);

      var match = toArray(this.sizeSelect.options).find(function (option) {
        return normalize(option.value) === wanted;
      });

      if (!match) return '';

      this.sizeSelect.value = match.value;

      return match.value;
    }

    renderFits(size) {
      if (this.currentEl) {
        this.currentEl.textContent = size || this.dataset.emptyLabel || '—';
      }

      if (!this.fitsEl) return;

      var entry = this.fitsBySize[normalize(size)];

      if (!entry) {
        this.fitsEl.hidden = true;
        return;
      }

      this.fitsEl.hidden = false;

      if (this.fitsNameEl) {
        var prefix = this.dataset.fitsPrefix || '';
        this.fitsNameEl.textContent = prefix ? prefix + ' ' + entry.label : entry.label;
      }

      if (this.fitsListEl) {
        this.fitsListEl.textContent = entry.combos.join(', ');
      }
    }

    /** Grey out cup values with no mapping for the chosen band, and vice versa. */
    refreshAvailability() {
      var band = this.bandSelect ? this.bandSelect.value : '';
      var cup = this.cupSelect ? this.cupSelect.value : '';
      var self = this;

      if (this.cupSelect) {
        this.markUnavailable(this.cupSelect, function (value) {
          return band ? Boolean(self.sizeByCombo[normalize(band + value)]) : true;
        });
      }

      if (this.bandSelect) {
        this.markUnavailable(this.bandSelect, function (value) {
          return cup ? Boolean(self.sizeByCombo[normalize(value + cup)]) : true;
        });
      }
    }

    markUnavailable(select, isAvailable) {
      toArray(select.options).forEach(function (option) {
        if (!option.value) return;
        option.disabled = !isAvailable(option.value);
      });
    }

    /** Drop Band/Cup back to their placeholders when they no longer describe the chosen size. */
    clearPairIfMismatched(size) {
      if (!this.bandSelect || !this.cupSelect) return;

      var band = this.bandSelect.value;
      var cup = this.cupSelect.value;
      if (!band || !cup) return;

      if (normalize(this.sizeByCombo[normalize(band + cup)]) === normalize(size)) return;

      this.bandSelect.value = '';
      this.cupSelect.value = '';
      this.refreshAvailability();
    }

    /** Select the first Band/Cup pair that resolves to `size`, so the row is never half-empty. */
    prefillFromSize(size) {
      if (this.dataset.prefill !== 'true') return;
      if (!this.bandSelect || !this.cupSelect || !size) return;
      if (this.bandSelect.value && this.cupSelect.value) return;

      var entry = this.fitsBySize[normalize(size)];
      if (!entry) return;

      var wanted = {};
      entry.combos.forEach(function (combo) {
        wanted[normalize(combo)] = true;
      });

      var bands = toArray(this.bandSelect.options);
      var cups = toArray(this.cupSelect.options);

      for (var b = 0; b < bands.length; b++) {
        if (!bands[b].value) continue;

        for (var c = 0; c < cups.length; c++) {
          if (!cups[c].value) continue;

          if (wanted[normalize(bands[b].value + cups[c].value)]) {
            this.bandSelect.value = bands[b].value;
            this.cupSelect.value = cups[c].value;
            this.refreshAvailability();
            return;
          }
        }
      }
    }

    /* ---------- product form sync ---------- */

    host() {
      return this.closest('product-info');
    }

    productForm() {
      var host = this.host();
      var formId = host && host.getAttribute('form');
      return formId ? document.forms[formId] || null : null;
    }

    variantPicker() {
      return (this.host() || document).querySelector('variant-picker');
    }

    /** Push the chosen size to every other block on the page that carries the same option. */
    propagate(size, event) {
      this.pushToVariantPicker(size, event);
      this.syncBundleSelects(size);
    }

    /** Mirror the chosen size onto the product form so the cart gets the right variant. */
    pushToVariantPicker(size, event) {
      if (!this.shouldSync || this.syncing || !size) return;
      if (this.driveVariantPicker(size)) return;

      this.publishOptionChange(size, event);
    }

    /**
     * Keep the Buy 1, 2, 3 block on the same size. Its selects carry a 0-based option index
     * (the variant pickers use a 1-based one) and it rebuilds every tier from a single delegated
     * change event, so move them all and then fire once.
     */
    syncBundleSelects(size) {
      if (!this.syncBundle || this.syncing || !size) return;

      var position = parseInt(this.dataset.optionPosition || '0', 10);
      if (position < 1) return;

      var scope = this.host() || document;
      var selects = toArray(
        scope.querySelectorAll('.bogo-option-select[data-option-index="' + (position - 1) + '"]')
      );
      if (!selects.length) return;

      var wanted = normalize(size);
      var changed = false;

      selects.forEach(function (select) {
        var match = toArray(select.options).find(function (option) {
          return normalize(option.value) === wanted;
        });

        if (!match || select.value === match.value) return;

        select.value = match.value;
        changed = true;
      });

      if (!changed) return;

      // The tier card the shopper is on is the one that feeds the cart, so trigger that row.
      var activeCard = scope.querySelector('.bogo-card--tier.is-active');
      var tier = activeCard && activeCard.getAttribute('data-bogo-tier');

      var trigger =
        selects.find(function (select) {
          return (!tier || select.getAttribute('data-tier') === tier) && select.getAttribute('data-item') === '1';
        }) || selects[0];

      this.dispatchSync(trigger);
    }

    /** Preferred path — move the theme's own picker, whichever style it is rendered in. */
    driveVariantPicker(size) {
      var picker = this.variantPicker();
      if (!picker) return false;

      var control = this.findPickerControl(picker, size);
      if (!control) return false;

      if (control.tagName === 'OPTION') {
        var select = control.parentElement;
        if (select.value !== control.value) {
          select.value = control.value;
          this.dispatchSync(select);
        }
      } else if (!control.checked) {
        control.checked = true;
        this.dispatchSync(control);
      }

      return true;
    }

    /**
     * Dropdown, button, swatch and variant pickers all tag their controls with the 1-based
     * option position, so this resolves the right one whatever `picker_type` the merchant picked.
     */
    findPickerControl(picker, size) {
      var wanted = normalize(size);
      var position = this.dataset.optionPosition;
      var candidates = [];

      if (position && position !== '0') {
        candidates = toArray(
          picker.querySelectorAll(
            'option[data-option-index="' +
              position +
              '"], input[type="radio"][data-option-index="' +
              position +
              '"]'
          )
        );
      }

      if (!candidates.length && this.dataset.optionSlug) {
        var group = picker.querySelector('[data-option-slug="' + this.dataset.optionSlug + '"]');
        if (group) candidates = toArray(group.querySelectorAll('option, input[type="radio"]'));
      }

      return candidates.find(function (control) {
        return normalize(control.value) === wanted;
      });
    }

    /**
     * Guards only against a synchronous echo — anything the theme sends back later arrives on its
     * own task and is idempotent, so the flag must not outlive the call that raised it.
     *
     * ProductInfo also puts focus back on the control a change came from once its re-render
     * lands (assets/theme.js — `activeElement.focus()`), and on iOS focusing a <select> opens
     * its option list. Nobody touched this one, so that reads as a dropdown springing open by
     * itself. It reads the id synchronously while the event is dispatching, so withholding it
     * for exactly that long skips the focus step and nothing else.
     */
    dispatchSync(element) {
      this.syncing = true;

      var id = element.tagName === 'SELECT' ? element.id : '';
      if (id) element.id = '';

      try {
        element.dispatchEvent(new Event('change', { bubbles: true }));
      } finally {
        if (id) element.id = id;
        this.syncing = false;
      }
    }

    /**
     * Fallback for templates that carry no variant picker block: announce the option change the
     * same way the picker would, so ProductInfo re-renders the price, media and variant input.
     */
    publishOptionChange(size, event) {
      var pubsub = window.theme && window.theme.pubsub;
      if (!pubsub || !pubsub.publish || !this.sizeSelect) return;

      var position = parseInt(this.dataset.optionPosition || '0', 10);
      if (position < 1) return;

      var wanted = normalize(size);

      var option = toArray(this.sizeSelect.options).find(function (item) {
        return normalize(item.value) === wanted;
      });

      var valueId = option && option.getAttribute('data-option-value-id');
      if (!valueId) return;

      var values = (this.dataset.selectedOptionValues || '').split(',').filter(Boolean);
      if (values.length < position) return;

      values[position - 1] = valueId;
      this.dataset.selectedOptionValues = values.join(',');

      // The same focus restore as in `dispatchSync`, reached the other way: ProductInfo focuses
      // `target.parentElement.id`, which here is the size select itself, so on iOS its option
      // list opens on its own the moment a Band and Cup pair resolves to a size. Nothing of ours
      // is re-rendered, so there is no focus to restore — a detached twin of the option carries
      // the same attributes with no id for the lookup to find.
      var detached = document.createElement('select');
      var twin = option.cloneNode(true);
      detached.appendChild(twin);

      this.syncing = true;

      try {
        pubsub.publish(pubsub.PUB_SUB_EVENTS.optionValueSelectionChange, {
          data: {
            // ProductInfo ignores anything raised outside itself, so the target has to be ours.
            event: event && this.contains(event.target) ? event : { target: this.sizeSelect },
            target: twin,
            selectedOptionValues: values,
          },
        });
      } finally {
        this.syncing = false;
      }
    }

    /** The shopper changed the size elsewhere on the page — follow along. */
    adoptVariant(variant) {
      if (!this.shouldSync || this.syncing || !this.ready) return;

      var value = this.sizeFromVariant(variant);
      if (!value) return;
      if (normalize(value) === normalize(this.selectedSize())) return;

      this.syncing = true;

      if (this.setSizeSelect(value)) {
        this.clearSelectionError();
        this.renderFits(value);
        this.clearPairIfMismatched(value);
        this.setNote('');
      }

      this.syncing = false;
    }

    sizeFromVariant(variant) {
      if (!variant) return '';

      var options = variant.options || [];
      var position = parseInt(this.dataset.optionPosition || '0', 10);

      if (position > 0 && options[position - 1]) return options[position - 1];

      var self = this;

      var matched = options.find(function (value) {
        return Boolean(self.fitsBySize[normalize(value)]);
      });

      return matched || '';
    }

    setNote(message) {
      if (!this.noteEl) return;

      this.noteEl.textContent = message || '';
      this.noteEl.hidden = !message;
    }
  }

  if (!customElements.get('size-fit-finder')) {
    customElements.define('size-fit-finder', SizeFitFinder);
  }
})();
